import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Alert,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../../components/ui/Button';
import { useResponsiveStyles } from '../../hooks/useResponsiveStyles';
import theme from '../../theme';
import { supabase } from '../../api/supabase';

interface VehicleType {
  make_model: string;
  year: number;
  count: number;
  current_rate: number;
  cost_per_month: number;
  contract_type?: string;
  vehicles: Array<{ id: string; license_plate: string; monthly_rental_rate: number; contract_type?: string }>;
}

export const VehicleRateSettingsScreen = ({ navigation }: any) => {
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<VehicleType | null>(null);
  const [newRate, setNewRate] = useState('');
  const [newCost, setNewCost] = useState('');
  const [updating, setUpdating] = useState(false);
  const [searchText, setSearchText] = useState('');
  const responsive = useResponsiveStyles();

  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  const fetchVehicleTypes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all vehicles with their rates
      const { data: vehicles, error: vehicleError } = await supabase
        .from('vehicles')
        .select('id, license_plate, make_model, year, monthly_rental_rate, contract_type')
        .order('make_model', { ascending: true });

      if (vehicleError) throw vehicleError;

      // Get vehicle costs
      const { data: costs, error: costError } = await supabase
        .from('vehicle_costs')
        .select('make_model, year, cost_per_month');

      if (costError) console.warn('Error fetching costs:', costError);

      if (!vehicles) {
        setVehicleTypes([]);
        return;
      }

      // Create cost lookup map
      const costMap = new Map<string, number>();
      if (costs) {
        costs.forEach(cost => {
          const key = `${cost.make_model}_${cost.year}`;
          costMap.set(key, cost.cost_per_month || 0);
        });
      }

      // Group vehicles by make_model + year + contract_type
      const typeMap = new Map<string, VehicleType>();

      vehicles.forEach(vehicle => {
        const key = `${vehicle.make_model}_${vehicle.year || 'unknown'}`;
        
        if (!typeMap.has(key)) {
          typeMap.set(key, {
            make_model: vehicle.make_model,
            year: vehicle.year || 0,
            contract_type: vehicle.contract_type,
            count: 0,
            current_rate: vehicle.monthly_rental_rate || 0,
            cost_per_month: costMap.get(key) || 0,
            vehicles: []
          });
        }

        const type = typeMap.get(key)!;
        type.count++;
        type.vehicles.push({
          id: vehicle.id,
          license_plate: vehicle.license_plate,
          monthly_rental_rate: vehicle.monthly_rental_rate || 0,
          contract_type: vehicle.contract_type
        });
        
        // Use the most common rate or highest rate as current_rate
        if (vehicle.monthly_rental_rate && vehicle.monthly_rental_rate > type.current_rate) {
          type.current_rate = vehicle.monthly_rental_rate;
        }
      });

      const sortedTypes = Array.from(typeMap.values()).sort((a, b) => {
        // Sort by make_model first, then by year
        if (a.make_model !== b.make_model) {
          return a.make_model.localeCompare(b.make_model);
        }
        return (b.year || 0) - (a.year || 0); // Newer years first
      });

      setVehicleTypes(sortedTypes);
    } catch (err) {
      console.error('Error fetching vehicle types:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRate = (vehicleType: VehicleType) => {
    setEditingType(vehicleType);
    setNewRate(vehicleType.current_rate.toString());
    setNewCost(vehicleType.cost_per_month.toString());
  };

  const handleUpdateRate = async () => {
    console.log('handleUpdateRate called with:', { editingType, newRate });
    
    if (!editingType || !newRate) {
      console.log('Missing editingType or newRate, returning early');
      return;
    }

    const rateValue = parseFloat(newRate);
    if (isNaN(rateValue) || rateValue < 0) {
      Alert.alert('Invalid Rate', 'Please enter a valid positive number for the monthly rate.');
      return;
    }

    console.log('Proceeding with update for rate:', rateValue);
    
    // Skip confirmation for now - direct update
    confirmUpdateRate();
  };

  const confirmUpdateRate = async () => {
    if (!editingType || !newRate) {
      console.log('Missing editingType or newRate:', { editingType, newRate });
      return;
    }

    try {
      setUpdating(true);
      const rateValue = parseFloat(newRate);
      const costValue = parseFloat(newCost) || 0;
      
      console.log('Updating rates for:', {
        vehicleType: editingType.make_model,
        year: editingType.year,
        newRate: rateValue,
        newCost: costValue,
        vehicleCount: editingType.vehicles.length
      });

      // Update all vehicles of this type
      const vehicleIds = editingType.vehicles.map(v => v.id);
      
      console.log('Vehicle IDs to update:', vehicleIds);
      
      const { data, error: updateError } = await supabase
        .from('vehicles')
        .update({ monthly_rental_rate: rateValue })
        .in('id', vehicleIds)
        .select();

      console.log('Supabase update result:', { data, error: updateError });

      if (updateError) throw updateError;

      // Try to update or insert vehicle cost
      // Note: vehicle_costs table has no unique constraint on (make_model, year)
      // So we'll try to find existing record first
      const { data: existingCosts, error: fetchError } = await supabase
        .from('vehicle_costs')
        .select('id')
        .eq('make_model', editingType.make_model)
        .eq('year', editingType.year);
      
      if (fetchError) {
        console.warn('Error fetching vehicle_costs:', fetchError);
      }
      
      const existingCost = existingCosts && existingCosts.length > 0 ? existingCosts[0] : null;
      
      const costData = {
        make_model: editingType.make_model,
        year: editingType.year,
        cost_per_month: costValue,
        rent_per_month: rateValue,
        cost_excludes_vat: false,
        rent_excludes_vat: false,
        vat_rate: 0.05,
        credit_rule_days: 3
      };
      
      if (existingCost) {
        // Update existing
        const { error: costError } = await supabase
          .from('vehicle_costs')
          .update(costData)
          .eq('id', existingCost.id);
          
        if (costError) {
          console.warn('Error updating vehicle_costs:', costError);
        }
      } else {
        // Insert new
        const { error: costError } = await supabase
          .from('vehicle_costs')
          .insert(costData);
          
        if (costError) {
          console.warn('Error inserting vehicle_costs:', costError);
        }
      }

      if (Platform.OS === 'web') {
        alert(`نجح / Success\n\nتم تحديث السعر الشهري\nUpdated monthly rate for ${editingType.count}x ${editingType.make_model} ${editingType.year} vehicles to AED ${rateValue}`);
      } else {
        Alert.alert(
          'نجح / Success', 
          `تم تحديث السعر الشهري\nUpdated monthly rate for ${editingType.count}x ${editingType.make_model} ${editingType.year} vehicles to AED ${rateValue}`
        );
      }

      // Close modal and refresh data
      setEditingType(null);
      setNewRate('');
      setNewCost('');
      await fetchVehicleTypes();

    } catch (err) {
      console.error('Error updating rates:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to update rates';
      if (Platform.OS === 'web') {
        alert(`Error\n\n${errorMsg}`);
      } else {
        Alert.alert('Error', errorMsg);
      }
    } finally {
      setUpdating(false);
    }
  };

  const calculateDailyRate = (monthlyRate: number) => {
    // Approximate daily rate using average 30 days per month
    return Math.round((monthlyRate / 30) * 100) / 100;
  };

  const renderVehicleType = ({ item }: { item: VehicleType }) => {
    const profit = item.current_rate - item.cost_per_month;
    const profitColor = profit > 0 ? '#10b981' : profit < 0 ? '#ef4444' : '#6b7280';
    
    return (
      <View style={styles.typeCard}>
        <View style={styles.typeHeader}>
          <View style={styles.typeInfo}>
            <Text style={styles.typeTitle}>
              {item.count}x {item.make_model}
            </Text>
            <Text style={styles.typeYear}>{item.year || 'Unknown Year'}</Text>
          </View>
          <TouchableOpacity 
            style={styles.editButton}
            onPress={() => handleEditRate(item)}
          >
            <Ionicons name="create-outline" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
        
        <View style={styles.rateInfo}>
          <View style={styles.rateRow}>
            <Text style={styles.rateLabel}>Monthly Cost:</Text>
            <Text style={[styles.rateValue, { color: '#ef4444' }]}>AED {item.cost_per_month}</Text>
          </View>
          <View style={styles.rateRow}>
            <Text style={styles.rateLabel}>Monthly Company Share:</Text>
            <Text style={[styles.rateValue, { color: '#10b981' }]}>AED {item.current_rate}</Text>
          </View>
          <View style={[styles.rateRow, { borderTopWidth: 1, borderTopColor: '#e5e7eb', paddingTop: 8, marginTop: 8 }]}>
            <Text style={[styles.rateLabel, { fontWeight: '600' as any }]}>Monthly Profit:</Text>
            <Text style={[styles.rateValue, { color: profitColor, fontWeight: '700' as any }]}>
              AED {profit.toFixed(2)}
            </Text>
          </View>
          <View style={styles.rateRow}>
            <Text style={styles.rateLabel}>Daily Rate (approx):</Text>
            <Text style={styles.dailyRateValue}>AED {calculateDailyRate(item.current_rate)}</Text>
          </View>
        </View>

        {/* Show rate variance if vehicles have different rates */}
        {item.vehicles.length > 1 && (
          <View style={styles.varianceInfo}>
            {(() => {
              const rates = item.vehicles.map(v => v.monthly_rental_rate).filter(r => r > 0);
              const uniqueRates = [...new Set(rates)];
              if (uniqueRates.length > 1) {
                return (
                  <Text style={styles.varianceText}>
                    ⚠️ {uniqueRates.length} different rates: {uniqueRates.join(', ')}
                  </Text>
                );
              }
              return null;
            })()}
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading vehicle types...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>Error: {error}</Text>
        <Button title="Retry" onPress={fetchVehicleTypes} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerTitle}>Vehicle Pricing</Text>
          <Text style={styles.headerSubtitle}>
            Set monthly costs and company share rates by vehicle type
          </Text>
        </View>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#999" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by make, model, or year..."
          value={searchText}
          onChangeText={setSearchText}
        />
        {searchText.length > 0 && (
          <TouchableOpacity onPress={() => setSearchText('')}>
            <Ionicons name="close-circle" size={20} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      <FlatList
        data={vehicleTypes.filter(vt => {
          if (!searchText) return true;
          const search = searchText.toLowerCase();
          return (
            vt.make_model.toLowerCase().includes(search) ||
            vt.year.toString().includes(search)
          );
        })}
        keyExtractor={(item) => `${item.make_model}_${item.year}`}
        renderItem={renderVehicleType}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="pricetag-outline" size={64} color="#d1d5db" />
            <Text style={styles.emptyText}>No vehicles found</Text>
            <Text style={styles.emptySubtext}>
              {searchText ? 'Try a different search term' : 'Add vehicles to set pricing'}
            </Text>
          </View>
        }
      />

      {/* Edit Rate Modal */}
      <Modal
        visible={editingType !== null}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setEditingType(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              Edit Pricing for {editingType?.make_model} {editingType?.year}
            </Text>
            <Text style={styles.modalSubtitle}>
              {editingType?.count} vehicle{editingType?.count !== 1 ? 's' : ''} will be updated
            </Text>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Monthly Cost (AED)</Text>
              <TextInput
                style={styles.input}
                value={newCost}
                onChangeText={setNewCost}
                placeholder="Enter monthly cost"
                keyboardType="numeric"
                editable={!updating}
              />
              <Text style={styles.inputHelper}>
                Ownership cost per vehicle per month
              </Text>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Monthly Company Share (AED)</Text>
              <TextInput
                style={styles.input}
                value={newRate}
                onChangeText={setNewRate}
                placeholder="Enter monthly company share"
                keyboardType="numeric"
                editable={!updating}
              />
              <Text style={styles.inputHelper}>
                Company share charged to drivers • Daily rate (approx): AED {newRate ? calculateDailyRate(parseFloat(newRate) || 0) : '0.00'}
              </Text>
            </View>

            {newRate && newCost && (
              <View style={[styles.inputContainer, { backgroundColor: '#f9fafb', padding: 12, borderRadius: 8 }]}>
                <Text style={styles.inputLabel}>Profit Calculation</Text>
                <Text style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
                  Rent: AED {parseFloat(newRate).toFixed(2)} - Cost: AED {parseFloat(newCost).toFixed(2)}
                </Text>
                <Text style={{ fontSize: 18, fontWeight: '700' as any, color: (parseFloat(newRate) - parseFloat(newCost)) > 0 ? '#10b981' : '#ef4444', marginTop: 8 }}>
                  Profit: AED {(parseFloat(newRate) - parseFloat(newCost)).toFixed(2)} per vehicle/month
                </Text>
              </View>
            )}

            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setEditingType(null)}
                disabled={updating}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalButton, styles.saveButton, updating && styles.disabledButton]}
                onPress={handleUpdateRate}
                disabled={updating || !newRate}
              >
                {updating ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text style={styles.saveButtonText}>Update Pricing</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  listContainer: {
    padding: 16,
  },
  typeCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  typeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  typeInfo: {
    flex: 1,
  },
  typeTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  typeYear: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  editButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 16,
    marginTop: 12,
    padding: 12,
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  rateInfo: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    paddingTop: 12,
  },
  rateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  rateLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  rateValue: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  dailyRateValue: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  varianceInfo: {
    marginTop: 8,
    padding: 8,
    backgroundColor: theme.colors.warning + '20',
    borderRadius: 6,
  },
  varianceText: {
    fontSize: 12,
    color: theme.colors.warning,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.background,
  },
  inputHelper: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  cancelButtonText: {
    color: theme.colors.textPrimary,
    fontSize: 16,
    fontWeight: '500',
  },
  saveButton: {
    backgroundColor: theme.colors.primary,
  },
  saveButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '500',
  },
  disabledButton: {
    opacity: 0.6,
  },
});
